import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { optInPostHog, optOutPostHog, initPostHog } from '@/lib/posthog';
import { trackEvent } from '@/lib/analytics';
import { updateGTMConsent } from '@/lib/gtm';

/**
 * Cookie Banner Component
 * GDPR-compliant cookie consent banner with granular controls
 */
export const CookieBanner = () => {
  const { t } = useTranslation('common');
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);

  useEffect(() => {
    // Check if user has already made a choice
    const consentGiven = localStorage.getItem('cookie_consent_given');
    if (!consentGiven) {
      // Show banner if no consent recorded
      setShowBanner(true);
    } else {
      // Load saved preferences
      const savedAnalytics = localStorage.getItem('cookie_analytics') !== 'false';
      setAnalyticsEnabled(savedAnalytics);

      // Initialize PostHog based on saved preferences (production only)
      if (savedAnalytics && import.meta.env.PROD) {
        initPostHog();
      }
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('cookie_consent_given', 'true');
    localStorage.setItem('cookie_consent', 'granted'); // App-origin GTM consent key
    localStorage.setItem('cookie_analytics', 'true');
    localStorage.setItem('posthog_opt_out', 'false');

    setShowBanner(false);
    setAnalyticsEnabled(true);

    // Update GTM consent
    updateGTMConsent(true);

    // Initialize PostHog (production only)
    if (import.meta.env.PROD) {
      initPostHog();
    }

    // Track consent given
    trackEvent('cookie_consent_accepted', {
      analytics: true,
    });
  };

  const handleRejectAll = () => {
    localStorage.setItem('cookie_consent_given', 'true');
    localStorage.setItem('cookie_consent', 'denied'); // App-origin GTM consent key
    localStorage.setItem('cookie_analytics', 'false');
    localStorage.setItem('posthog_opt_out', 'true');

    setShowBanner(false);
    setAnalyticsEnabled(false);

    // Update GTM consent
    updateGTMConsent(false);

    // Opt out of PostHog
    optOutPostHog();
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookie_consent_given', 'true');
    localStorage.setItem('cookie_consent', analyticsEnabled ? 'granted' : 'denied'); // GTM consent key
    localStorage.setItem('cookie_analytics', analyticsEnabled.toString());

    // Update GTM consent
    updateGTMConsent(analyticsEnabled);

    if (analyticsEnabled) {
      localStorage.setItem('posthog_opt_out', 'false');
      optInPostHog();
      // Initialize PostHog (production only)
      if (import.meta.env.PROD) {
        initPostHog();
      }
    } else {
      localStorage.setItem('posthog_opt_out', 'true');
      optOutPostHog();
    }

    setShowBanner(false);
    setShowSettings(false);

    // Track preferences saved
    if (analyticsEnabled) {
      trackEvent('cookie_consent_saved', {
        analytics: true,
      });
    }
  };

  if (!showBanner) {
    return null;
  }

  return (
    <>
      {/* Cookie Banner — sits above the mobile bottom nav (h-14 + safe area) so it never covers
          the navigation; flush to the bottom on md+ where the bottom nav is hidden. */}
      <div className='fixed bottom-[calc(3.5rem+env(safe-area-inset-bottom))] left-0 right-0 z-50 border-t bg-background p-4 shadow-lg sm:px-6 md:bottom-0'>
        <div className='mx-auto flex max-w-7xl flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
          <div className='flex-1'>
            <h3 className='text-sm font-semibold text-foreground'>{t('cookieBanner.title', 'Cookie-Einstellungen')}</h3>
            <p className='mt-1 text-sm text-muted-foreground'>
              {t('cookieBanner.description', 'Wir verwenden Cookies und ähnliche Technologien, um Ihre Erfahrung zu verbessern und unsere Dienste zu analysieren. Sie können Ihre Präferenzen jederzeit ändern.')}
            </p>
          </div>

          <div className='flex flex-col gap-2 sm:flex-row sm:items-center'>
            <Button variant='outline' size='sm' onClick={() => setShowSettings(true)}>
              {t('cookieBanner.settings', 'Einstellungen')}
            </Button>
            <Button variant='outline' size='sm' onClick={handleRejectAll}>
              {t('cookieBanner.reject', 'Ablehnen')}
            </Button>
            <Button size='sm' onClick={handleAcceptAll}>
              {t('cookieBanner.accept', 'Alle akzeptieren')}
            </Button>
          </div>
        </div>
      </div>

      {/* Cookie Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className='sm:max-w-lg'>
          <DialogHeader>
            <DialogTitle>{t('cookieSettings.title', 'Cookie-Einstellungen')}</DialogTitle>
            <DialogDescription>{t('cookieSettings.description', 'Verwalten Sie Ihre Cookie-Präferenzen. Sie können diese jederzeit ändern.')}</DialogDescription>
          </DialogHeader>

          <div className='space-y-4 py-4'>
            {/* Analytics Toggle */}
            <div className='flex items-center justify-between'>
              <div className='flex-1'>
                <label htmlFor='analytics-toggle' className='text-sm font-medium text-foreground'>
                  {t('cookieSettings.analytics.title', 'Analytics')}
                </label>
                <p className='mt-1 text-xs text-muted-foreground'>{t('cookieSettings.analytics.description', 'Hilft uns zu verstehen, wie Benutzer unsere Website nutzen')}</p>
              </div>
              <Switch id='analytics-toggle' checked={analyticsEnabled} onCheckedChange={setAnalyticsEnabled} />
            </div>
          </div>

          <DialogFooter>
            <Button variant='outline' onClick={() => setShowSettings(false)}>
              {t('actions.cancel', 'Abbrechen')}
            </Button>
            <Button onClick={handleSavePreferences}>{t('actions.save', 'Speichern')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
